import { useEffect, useRef, useState } from 'react'
import { AppShell } from '../components/AppShell'
import { SignaturePad, type SignaturePadHandle } from '../components/SignaturePad'
import type { AuthenticatedSession } from '../api'
import {
  TUTORIAL_PATH,
  tutorialBack,
  tutorialIndex,
  tutorialNext,
  tutorialNextLabel,
  tutorialOnPath,
  type TutorialStep,
} from '../onboardingTutorial'

const MOCK_SESSION: AuthenticatedSession = {
  authenticated: true,
  identity: {
    id: 'id_onboarding_preview',
    party_id: 'party_onboarding_preview',
    kind: 'person',
    display_name: 'Alex Chen',
    email: 'alex@northwind.test',
  },
  memberships: [{
    organization_id: 'org_preview',
    organization_name: 'Northwind',
    role: 'admin',
  }],
  auth_method: 'federated_oidc',
}

/**
 * Click-through of the onboarding we intend to build. Real classes and the
 * real shell, no write API, so the first-run page can be judged as a page
 * rather than as a picture of one. Photographed by shots and walked by
 * edges; not linked from the product.
 */
export function OnboardingPreview() {
  const [step, setStep] = useState<TutorialStep>('welcome')
  const [dismissed, setDismissed] = useState(false)
  const at = tutorialIndex(step)
  const onPath = tutorialOnPath(step)

  const go = (s: TutorialStep) => setStep(s)
  const back = () => setStep(tutorialBack(step))
  const next = () => setStep(tutorialNext(step))

  // Arrow keys move the tutorial, except while someone is typing — a field
  // that steals Left/Right is a field, not a step.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (e.key === 'ArrowRight') setStep((s) => tutorialNext(s))
      if (e.key === 'ArrowLeft') setStep((s) => tutorialBack(s))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const body = (() => {
    switch (step) {
      case 'welcome':
        return <Welcome on={setStep} />
      case 'look-around':
        return <LookAround on={setStep} />
      case 'invite':
        return <Invite on={setStep} />
      case 'consent':
        return <Consent on={setStep} />
      case 'sign':
        return <DrawSignature on={setStep} />
      case 'signed':
        return <Signed on={setStep} />
      case 'detail':
        return <Record on={setStep} />
      case 'verify':
        return <Check on={setStep} />
      case 'started':
        return <WhatNext dismissed={dismissed} onDismiss={() => setDismissed(true)} on={setStep} />
    }
  })()

  const inShell = step === 'welcome' || step === 'look-around' || step === 'invite'
    || step === 'detail' || step === 'verify' || step === 'started'

  const page = (
    <div className="tutorial">
      <TutorialBar step={step} at={at} onPath={onPath} on={go} />
      <div className="tutorial-body">{body}</div>
      <TutorialFoot step={step} at={at} onPath={onPath} onBack={back} onNext={next} />
    </div>
  )

  if (!inShell) {
    return page
  }

  return (
    <AppShell session={MOCK_SESSION} onSignOut={() => {}}>
      {page}
    </AppShell>
  )
}

function TutorialBar({
  step,
  at,
  onPath,
  on,
}: {
  step: TutorialStep
  at: number
  onPath: boolean
  on: (s: TutorialStep) => void
}) {
  return (
    <header className="tutorial-bar">
      <div className="tutorial-bar-copy">
        <div className="page-label">Tutorial</div>
        <p className="tutorial-bar-title">
          {onPath ? 'Sending a practice document' : 'Off the main path'}
        </p>
        <p className="small faint">
          {onPath
            ? `Step ${at + 1} of ${TUTORIAL_PATH.length}: ${TUTORIAL_PATH[at].label}. Back and Next move you through it; nothing here is sent.`
            : 'This is a side door. Back to tutorial returns you to the practice path.'}
        </p>
      </div>
      <ol className="tutorial-dots" aria-label="Tutorial steps">
        {TUTORIAL_PATH.map((s, i) => (
          <li key={s.id}>
            <button
              type="button"
              className={`tutorial-dot${step === s.id ? ' is-active' : ''}${i < at ? ' is-done' : ''}`}
              aria-current={step === s.id ? 'step' : undefined}
              aria-label={`Step ${i + 1}, ${s.label}`}
              onClick={() => on(s.id)}
            >
              {i + 1}
            </button>
            <span className="tutorial-dot-label">{s.label}</span>
          </li>
        ))}
      </ol>
    </header>
  )
}

function TutorialFoot({
  step,
  at,
  onPath,
  onBack,
  onNext,
}: {
  step: TutorialStep
  at: number
  onPath: boolean
  onBack: () => void
  onNext: () => void
}) {
  const last = onPath && at === TUTORIAL_PATH.length - 1
  const first = onPath && at === 0
  return (
    <footer className="tutorial-foot">
      <button id="tutorial-back" className="btn btn-secondary" type="button" onClick={onBack} disabled={first}>
        {onPath ? 'Back' : 'Back to tutorial'}
      </button>
      <p className="tutorial-foot-status small" aria-live="polite">
        {tutorialNextLabel(step)}
      </p>
      <button id="tutorial-next" className="btn" type="button" onClick={onNext}>
        {last ? 'Start again' : onPath ? 'Next' : 'Back to tutorial'}
      </button>
    </footer>
  )
}

function Welcome({ on }: { on: (s: TutorialStep) => void }) {
  return (
    <main className="page">
      <div className="page-head">
        <div className="page-label">Your documents</div>
        <h1>Envelopes</h1>
        <p>Send one, then you will know the rest.</p>
      </div>

      <div className="onboarding-welcome">
        <div className="card">
          <h2>Practice document</h2>
          <p>
            A one-page sample, already placed, sent only to you. It does not count
            against the trial.
          </p>
          <button className="btn" type="button" onClick={() => on('consent')}>
            Send it to myself
          </button>
        </div>
        <div className="stack">
          <div className="card">
            <h2>Send a real document</h2>
            <p>Upload your own PDF and place the boxes.</p>
            <p className="small faint">
              That flow is already built. This mock follows the practice path.
            </p>
          </div>
          <div className="card">
            <h2>Invite a colleague</h2>
            <p>They send from the same organization.</p>
            <button className="btn btn-secondary" type="button" onClick={() => on('invite')}>
              Invite
            </button>
          </div>
        </div>
      </div>

      <p className="onboarding-mock-look">
        <button className="btn btn-link" type="button" onClick={() => on('look-around')}>
          Look around first
        </button>
      </p>
    </main>
  )
}

function LookAround({ on }: { on: (s: TutorialStep) => void }) {
  return (
    <main className="page">
      <div className="row-between page-head">
        <div>
          <div className="page-label">Your documents</div>
          <h1>Envelopes</h1>
          <p>Documents you have sent, and where each one stands.</p>
        </div>
        <button className="btn" type="button" onClick={() => on('welcome')}>
          Send a document
        </button>
      </div>

      <div className="card empty">
        <h2>Nothing sent yet</h2>
        <p>Upload a PDF, drop a signature box on it, and send it for signing.</p>
        <button className="btn" type="button" onClick={() => on('welcome')}>
          Send your first document
        </button>
      </div>
    </main>
  )
}

function Invite({ on }: { on: (s: TutorialStep) => void }) {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState('')

  return (
    <main className="page">
      <div className="page-head">
        <div className="page-label">Northwind</div>
        <h1>Invitations</h1>
        <p>
          Invited addresses can join this organization. With company sign-in on, staff at its domains also join when they sign in with your identity provider.
        </p>
      </div>

      {sent && <div className="notice notice-good">{sent}</div>}

      <div className="card">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (!email.trim()) return
            setSent(`${email.trim()} can now sign in. Nothing has been emailed. Send them the link yourself.`)
            setEmail('')
          }}
        >
          <label htmlFor="onboarding-invite-email">Email</label>
          <input
            id="onboarding-invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jordan@northwind.test"
          />
          <div className="row" style={{ marginTop: '1rem' }}>
            <button className="btn" type="submit">Invite</button>
            <button className="btn btn-ghost" type="button" onClick={() => on('welcome')}>
              Back to envelopes
            </button>
          </div>
        </form>
      </div>
    </main>
  )
}

function PracticePage({ signed }: { signed?: boolean }) {
  return (
    <div className="onboarding-mock-page">
      <p className="small faint">Northwind · Practice document</p>
      <h2>One-page sample</h2>
      <p>
        This is a stand-in for the bundled PDF. The signature box is already
        placed. Signing it produces a real record in the product; this preview
        only shows the screens.
      </p>
      <p>
        I agree that I have read this page, and that my signature below is mine.
      </p>
      <div className={`field-box${signed ? ' is-signed' : ' is-readonly'}`}>
        <span className="field-box-label">Alex Chen · Signature</span>
        {signed
          ? <span className="onboarding-mock-ink">A. Chen</span>
          : <span className="onboarding-mock-await">Sign here</span>}
      </div>
    </div>
  )
}

function Consent({ on }: { on: (s: TutorialStep) => void }) {
  return (
    <main className="page">
      <div className="page-head">
        <h1>Practice document</h1>
        <p>Alex Chen asked you to sign this document.</p>
      </div>

      <div className="grid-doc grid-doc-sign">
        <PracticePage />
        <div className="stack">
          <div className="card">
            <h2>Before you sign</h2>
            <p className="small muted">
              You are acting as alex@northwind.test, signed in with Google.
            </p>
            <p className="small muted">
              You are about to sign this document electronically. An electronic
              signature has the same legal effect as signing on paper. We will
              record when you opened the document, when you agreed to this, when
              you signed, and the IP address you did it from — and include that
              record with the signed document.
            </p>
            <p className="small muted">
              You can decline instead, and you can download a copy of the signed
              document afterwards to keep.
            </p>
            <button className="btn" type="button" onClick={() => on('sign')}>
              I agree to sign electronically
            </button>
          </div>
        </div>
      </div>
    </main>
  )
}

function DrawSignature({ on }: { on: (s: TutorialStep) => void }) {
  const pad = useRef<SignaturePadHandle>(null)
  const [hasInk, setHasInk] = useState(false)
  const [name, setName] = useState('Alex Chen')

  return (
    <main className="page">
      <div className="page-head">
        <h1>Practice document</h1>
        <p>Alex Chen asked you to sign this document.</p>
      </div>

      <div className="grid-doc grid-doc-sign">
        <PracticePage />
        <div className="stack">
          <div className="card">
            <h2>Your signature</h2>
            <p className="small muted">Draw your signature below using a mouse, trackpad or finger.</p>
            <SignaturePad ref={pad} onChange={setHasInk} />
            <div className="row" style={{ marginBottom: '1rem' }}>
              <button
                className="btn btn-ghost"
                type="button"
                onClick={() => {
                  pad.current?.clear()
                  setHasInk(false)
                }}
              >
                Clear
              </button>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="onboarding-typed">Your full name</label>
              <input
                id="onboarding-typed"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <button
              className="btn btn-lg"
              type="button"
              disabled={!hasInk || !name.trim()}
              onClick={() => on('signed')}
            >
              Sign document
            </button>
            <p className="small faint" style={{ marginTop: '.9rem', marginBottom: 0 }}>
              Signing seals the document cryptographically. Any later change to
              it becomes detectable by anyone who checks.
            </p>
          </div>
        </div>
      </div>
    </main>
  )
}

function Signed({ on }: { on: (s: TutorialStep) => void }) {
  return (
    <main className="page page-narrow">
      <div className="card">
        <h1>Signed</h1>
        <p className="muted">
          &ldquo;Practice document&rdquo; has been signed and sealed. A copy has been
          kept for Alex Chen.
        </p>
        <div className="notice notice-good" style={{ margin: '1rem 0' }}>
          <strong>Verified.</strong> The sealed document was checked immediately
          after signing and its signature is intact.
        </div>
        <table className="table">
          <tbody>
            <tr>
              <td className="muted">Document fingerprint</td>
              <td className="mono">a3f1c8e0b2d4…</td>
            </tr>
            <tr>
              <td className="muted">Size</td>
              <td>24,118 bytes</td>
            </tr>
            <tr>
              <td className="muted">Kept until</td>
              <td>25 Dec 2026, 00:00</td>
            </tr>
          </tbody>
        </table>
        <div className="row" style={{ marginTop: '1rem' }}>
          <button className="btn" type="button" onClick={() => on('detail')}>
            Open the record
          </button>
          <button className="btn btn-secondary" type="button">
            Download signed PDF
          </button>
        </div>
      </div>
    </main>
  )
}

function Record({ on }: { on: (s: TutorialStep) => void }) {
  return (
    <main className="page">
      <div className="page-head">
        <div className="page-label">Envelope</div>
        <h1>Practice document</h1>
        <p>Signed and sealed. The timeline is the record of what happened.</p>
      </div>

      <div className="grid-doc grid-doc-detail">
        <PracticePage signed />

        <div className="stack">
          <div className="card card-tight">
            <h3>Next steps</h3>
            <ul className="next-steps">
              <li>
                <button className="btn btn-block" type="button">Download signed PDF</button>
              </li>
              <li>
                <button className="btn btn-secondary btn-block" type="button">
                  Download certificate
                </button>
              </li>
              <li>
                <button className="btn btn-secondary btn-block" type="button" onClick={() => on('verify')}>
                  Check it on Verify
                </button>
              </li>
            </ul>
          </div>

          <div className="card card-tight">
            <h3>Timeline</h3>
            <ul className="timeline">
              <li>
                <span className="dot is-sealed" aria-hidden />
                <div>
                  <div className="evt-type">Sealed</div>
                  <div className="evt-meta">Just now · Alex Chen · google</div>
                </div>
              </li>
              <li>
                <span className="dot" aria-hidden />
                <div>
                  <div className="evt-type">Signed</div>
                  <div className="evt-meta">Just now · Alex Chen &lt;alex@northwind.test&gt;</div>
                </div>
              </li>
              <li>
                <span className="dot" aria-hidden />
                <div>
                  <div className="evt-type">Agreed to sign electronically</div>
                  <div className="evt-meta">Just now</div>
                </div>
              </li>
              <li>
                <span className="dot" aria-hidden />
                <div>
                  <div className="evt-type">Opened</div>
                  <div className="evt-meta">Just now</div>
                </div>
              </li>
              <li>
                <span className="dot" aria-hidden />
                <div>
                  <div className="evt-type">Sent</div>
                  <div className="evt-meta">Just now · sent to yourself</div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </main>
  )
}

function Check({ on }: { on: (s: TutorialStep) => void }) {
  const [checked, setChecked] = useState(false)

  return (
    <main className="page page-narrow">
      <div className="page-head">
        <h1>Verify a document</h1>
        <p>
          Check whether the signatures in a PDF still match what they signed,
          and who made them. They are read from the file itself, so this works
          for documents we have never seen. We also look up whether docsdone
          sealed this exact file.
        </p>
      </div>

      {!checked ? (
        <button
          type="button"
          className="dropzone"
          onClick={() => setChecked(true)}
        >
          <strong>Drop a PDF here</strong>
          <span className="small">or click to choose a file (this mock treats a click as the signed practice PDF)</span>
        </button>
      ) : (
        <div className="card">
          <h2>Practice document.pdf</h2>
          {/* What the real page says of a file docsdone sealed with a key
              nobody's machine trusts yet: the 'recognised' verdict. */}
          <div className="notice notice-good">
            <strong>Sealed by docsdone.</strong> docsdone recognises this exact
            file as one it sealed, and every signature still matches the bytes
            it signed. Nobody this machine trusts signed it, so that is
            docsdone&rsquo;s own record speaking: outside docsdone, the
            signatures show the bytes are unchanged, not who made them.
          </div>
          <div style={{ marginTop: '1rem' }}>
            <h3>Signature 1</h3>
            <table className="table">
              <tbody>
                <tr><td className="muted">Signed by</td><td>Alex Chen</td></tr>
                <tr><td className="muted">Integrity</td><td>Valid: bytes unchanged</td></tr>
                <tr><td className="muted">Issuer trust</td><td>Not trusted by this machine</td></tr>
              </tbody>
            </table>
          </div>
          <div className="row" style={{ marginTop: '1rem' }}>
            <button className="btn" type="button" onClick={() => on('started')}>
              Back to envelopes
            </button>
          </div>
        </div>
      )}
    </main>
  )
}

function WhatNext({
  dismissed,
  onDismiss,
  on,
}: {
  dismissed: boolean
  onDismiss: () => void
  on: (s: TutorialStep) => void
}) {
  return (
    <main className="page">
      <div className="row-between page-head">
        <div>
          <div className="page-label">Your documents</div>
          <h1>Envelopes</h1>
          <p>Documents you have sent, and where each one stands.</p>
        </div>
        <button className="btn" type="button">Send a document</button>
      </div>

      {!dismissed && (
        <div className="onboarding-next">
          <div className="row-between">
            <h2>What you can do</h2>
            <button className="btn btn-ghost small" type="button" onClick={onDismiss}>
              Dismiss
            </button>
          </div>
          <div className="card">
            <h3>Send a real one</h3>
            <p>
              The practice document is signed. Upload your own PDF and place the
              boxes.
            </p>
            <button className="btn" type="button">Send a document</button>
          </div>
          <ul className="onboarding-next-quiet">
            <li><button className="btn btn-link" type="button" onClick={() => on('invite')}>Invite a colleague</button></li>
            <li><button className="btn btn-link" type="button" onClick={() => on('verify')}>Verify a signed file</button></li>
            <li><button className="btn btn-link" type="button">Save a template</button></li>
            <li>
              <button className="btn btn-link" type="button">Company sign-in</button>
              <span className="faint small"> Optional, for larger teams</span>
            </li>
          </ul>
        </div>
      )}

      <div className="doc-card">
        <div className="doc-card-header">
          <h2>Recent</h2>
          <span className="faint small">1 shown</span>
        </div>
        <div className="doc-rows">
          <div className="doc-line">
            <button className="doc-row" type="button" onClick={() => on('detail')}>
              <div className="doc-icon" aria-hidden>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </div>
              <div className="doc-text">
                <div className="doc-title">Practice document</div>
                <div className="doc-meta faint small">Just now · 1 page · you are a signer</div>
              </div>
              <span className="doc-status doc-status-signed">Signed</span>
            </button>
          </div>
        </div>
      </div>
    </main>
  )
}
